import { Breakpoint, ButtonLevel, ButtonLevels, ButtonSize, ButtonSizes, WindowGlobal } from '../types/globalTypes';
import { EventTypes, environment, version } from '../global/AppInsights/constants';

export type ShadowRootLegacy = ShadowRoot & { tagName: string };

/**
 * Generates a unique component ID
 * @param   {string}  namespace   The unique id's namespace (e.g. ads)
 * @returns {string}              A generated random id (e.g. ads-1234)
 */
export const generateId = (namespace = 'ads'): string => {
  return `${namespace}-${generateRandomString()}`;
};

/**
 * Generates a random string with only valid HTML ID characters
 * @returns {string}              A generated random string (letters, digits, hyphens, underscores only)
 */
export const generateRandomString = (): string => {
  // Generate a random string with only valid HTML ID characters (letters, digits)
  // Avoid special characters like colons that are invalid in ARIA attributes
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 16; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Generates a size class based on a button size
 * @param {ButtonSize}  size    The size of the button (e.g. ButtonSizes.SMALL)
 * @returns {string}            The button size class (e.g. 'is-small')
 */
export const getClassFromSize = (size: ButtonSize): string => {
  switch (size) {
    case ButtonSizes.MEDIUM:
      return 'is-medium';
    case ButtonSizes.SMALL:
      return 'is-small';
    default:
      return '';
  }
};

/**
 * Generates a level class based on button a level
 * @param {ButtonLevel} level   The level of the button (e.g. ButtonLevels.PRIMARY)
 * @returns {string}            The button level class (e.g. 'is-primary')
 */
export const getClassFromLevel = (level: ButtonLevel): string => {
  switch (level) {
    case ButtonLevels.PRIMARY:
      return 'is-primary';
    case ButtonLevels.SECONDARY:
      return 'is-secondary';
    case ButtonLevels.TERTIARY:
      return 'is-tertiary';
    default:
      return '';
  }
};

/**
 * Given a breakpoint it will return the appropriate class for the media-queries (breakpoints-up).
 * @param {Breakpoint} bp The breakpoint.
 * @returns {string} The class.
 */
export const getMediaQueryBreakpointClassUp = (bp: Breakpoint): string => `${bp.toString()}-up`;

/**
 * Given a breakpoint it will return the appropriate class for the media-queries (breakpoints-down).
 * @param {Breakpoint} bp The breakpoint.
 * @returns {string} The class.
 */
export const getMediaQueryBreakpointClassDown = (bp: Breakpoint): string => `${bp.toString()}-down`;

/**
 * Display an ADS warning with the given message
 * @param {string}  message       The error message to show
 * @param {string}  component     The reference to the component
 * @returns {void}
 */
export const showErrorMessage = (message: string, component: HTMLElement): void => {
  try {
    (window as WindowGlobal)['AppInsights'].trackEvent(
      {
        name: EventTypes.ADS_CONSOLE_ERROR as string,
      },
      {
        component: component.tagName.toLowerCase(),
        componentId: component.id,
        message: message,
        environment,
        version,
        host: window.location.host,
      }
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('[APP INSIGHTS] Could not send console error', err);
  }
  // eslint-disable-next-line no-console
  console.warn(`ADS-Warning: ${message}`);
};
